// assets/hero-parallax.js
(() => {
  
  if (window.__heroParallaxLoaded) return;
  window.__heroParallaxLoaded = true;

  const INSTANCES = new WeakMap();

  class HeroParallax {
    constructor(section) {
      this.section = section;

      // Detectar si la sección es la variante v2 (cacao)
      this.isV2 = section.hasAttribute('data-hero-parallax-v2');

      // Fondos: v1 => .hero-bg ; v2 => .hero-bg--v2 (desktop + mobile en ambos casos)
      this.bgs = Array.from(
        section.querySelectorAll(this.isV2 ? '.hero-bg--v2' : '.hero-bg')
      );

      // Elementos parallax (v1 = leafs; v2 = cacao)
      this.leafA = section.querySelector(this.isV2 ? '.leaf-a--v2' : '.leaf-a');
      this.leafB = section.querySelector(this.isV2 ? '.leaf-b--v2' : '.leaf-b');

      // ===== CONFIG scroll-driven (base) =====
      this.FACTOR_BG = 0.25;
      this.FACTOR_A  = 0.45;
      this.FACTOR_B  = 0.60;

      // Escala de parallax en mobile
      this.MOBILE_BREAKPOINT = 768;
      this.MOBILE_PARALLAX_SCALE = 0.8;

      // Rotación total a lo largo del progreso 0..1 (solo v1)
      this.A_ROTATION_OVER_SCROLL = 10;   // deg
      this.B_ROTATION_OVER_SCROLL = -8;   // deg

      // Offsets permanentes (v1 invertidas 180°)
      this.A_ROT_OFFSET = 180;
      this.B_ROT_OFFSET = 180;

      // Escalas base + extra con scroll
      this.A_BASE_SCALE = 1.6;
      this.A_EXTRA_SCALE_OVER_SCROLL = 0.35;
      this.B_BASE_SCALE = 1.45;
      this.B_EXTRA_SCALE_OVER_SCROLL = 0.30;

      // ===== Ajustes para v2 (cacao) =====
      if (this.isV2) {
        // Sin rotación para cacao
        this.A_ROTATION_OVER_SCROLL = 0;
        this.B_ROTATION_OVER_SCROLL = 0;
        this.A_ROT_OFFSET = 0;
        this.B_ROT_OFFSET = 0;

        // Parallax un poco más suave
        this.FACTOR_A *= 0.95;
        this.FACTOR_B *= 0.95;

        // Reducir tamaño un poco (≈10%) y suavizar crecimiento (≈15%)
        const scaleMultiplier = parseFloat(section.dataset.scale || '0.90'); // opcional: data-scale en <section>
        const growMultiplier  = 0.85;

        this.A_BASE_SCALE *= scaleMultiplier;
        this.B_BASE_SCALE *= scaleMultiplier;
        this.A_EXTRA_SCALE_OVER_SCROLL *= growMultiplier;
        this.B_EXTRA_SCALE_OVER_SCROLL *= growMultiplier;
      }

      // Easing del progreso (opcional)
      this.USE_EASING = true;
      this.easeOutCubic = t => 1 - Math.pow(1 - t, 3);

      // Estado
      this.y = 0;            // desplazamiento relativo del section
      this.rafId = null;
      this.active = false;

      // Bind
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.tick     = this.tick.bind(this);

      // Cálculo inicial dependiente de viewport
      this.recomputeFactors();

      // Observar visibilidad del section (solo animamos si está en viewport)
      this.io = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            this.active = entry.isIntersecting;
            if (this.active) this.start(); else this.stop();
          });
        },
        { root: null, threshold: 0 }
      );
      this.io.observe(this.section);

      // Marcar como inicializado para no duplicar
      this.section.dataset.parallaxBound = 'true';
    }

    // ====== NUEVO: helpers para leer y convertir --bg-bleed a px ======
    resolveLengthToPx(str) {
      if (!str) return 0;
      const s = String(str).trim();
      const n = parseFloat(s);
      if (!isFinite(n)) return 0;

      if (s.endsWith('px')) return n;

      if (s.endsWith('vh')) {
        const vh = window.innerHeight || document.documentElement.clientHeight;
        return vh * (n / 100);
      }
      if (s.endsWith('vw')) {
        const vw = window.innerWidth || document.documentElement.clientWidth;
        return vw * (n / 100);
      }
      if (s.endsWith('rem')) {
        const fs = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
        return n * fs;
      }
      if (s.endsWith('%')) {
        const h = this.section.getBoundingClientRect().height || 0;
        return h * (n / 100);
      }
      // sin unidad => px
      return n;
    }

    readBleedPx() {
      const cs = getComputedStyle(this.section);
      const raw = (cs.getPropertyValue('--bg-bleed') || '').trim();
      // Fallback razonable si no está definida la variable en CSS
      return this.resolveLengthToPx(raw || '12vh');
    }
    // ================================================================

    isMobile() {
      return window.matchMedia(`(max-width: ${this.MOBILE_BREAKPOINT}px)`).matches;
    }

    recomputeFactors() {
      const mobile = this.isMobile();
      this.FB  = mobile ? this.FACTOR_BG * this.MOBILE_PARALLAX_SCALE : this.FACTOR_BG;
      this.FA  = mobile ? this.FACTOR_A  * this.MOBILE_PARALLAX_SCALE : this.FACTOR_A;
      this.FBb = mobile ? this.FACTOR_B  * this.MOBILE_PARALLAX_SCALE : this.FACTOR_B;

      // NUEVO: calcular el sangrado de fondo en px (depende del viewport)
      this.bgBleedPx = this.readBleedPx();
    }

    onScroll() {
      const rect = this.section.getBoundingClientRect();
      this.y = -rect.top; // al bajar scroll, aumenta
    }

    onResize() {
      this.recomputeFactors();
      this.onScroll();
      // Forzamos un frame
      this.applyTransforms();
    }

    getScrollProgress() {
      const rect = this.section.getBoundingClientRect();
      const vh   = window.innerHeight || document.documentElement.clientHeight;
      const totalScrollable = rect.height + vh;  // tramo en el que el section interactúa
      const passed = vh - rect.top;
      let progress = passed / totalScrollable;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;
      return this.USE_EASING ? this.easeOutCubic(progress) : progress;
    }

    applyTransforms() {
      const progress = this.getScrollProgress();

      // Parallax Y
      const bgYRaw = this.y * this.FB;
      const aY     = this.y * this.FA;
      const bY     = this.y * this.FBB;

      
    }

    // === 
    applyTransforms() {
      const progress = this.getScrollProgress();

      // Parallax Y
      const bgYRaw = this.y * this.FB;
      const aY     = this.y * this.FA;
      const bY     = this.y * this.FBb;

      
      const max = this.bgBleedPx || 0;
      const bgY = Math.max(Math.min(bgYRaw, max), -max);

      // Rotaciones + offsets
      const aRot = this.A_ROTATION_OVER_SCROLL * progress + this.A_ROT_OFFSET;
      const bRot = this.B_ROTATION_OVER_SCROLL * progress + this.B_ROT_OFFSET;

      // Escalas por scroll
      const aScale = this.A_BASE_SCALE + this.A_EXTRA_SCALE_OVER_SCROLL * progress;
      const bScale = this.B_BASE_SCALE + this.B_EXTRA_SCALE_OVER_SCROLL * progress;

      // Fondo: aplicar a TODAS las capas .hero-bg / .hero-bg--v2 
      this.bgs.forEach(bg => {
        if (bg) bg.style.transform = `translate3d(0, ${bgY}px, 0)`;
      });

      // v1 => incluye rotate(); v2 (cacao) => sin rotate()
      if (this.leafA) {
        const rotA = this.isV2 ? '' : ` rotate(${aRot}deg)`;
        this.leafA.style.transform = `translate3d(0, ${aY}px, 0)${rotA} scale(${aScale})`;
      }
      if (this.leafB) {
        const rotB = this.isV2 ? '' : ` rotate(${bRot}deg)`;
        this.leafB.style.transform = `translate3d(0, ${bY}px, 0)${rotB} scale(${bScale})`;
      }
    }

    tick() {
      if (!this.active) return;
      this.applyTransforms();
      this.rafId = window.requestAnimationFrame(this.tick);
    }

    start() {
      if (this.rafId) return;
      this.onScroll();
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      this.rafId = window.requestAnimationFrame(this.tick);
    }

    stop() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      
    }

    destroy() {
      this.stop();
      if (this.io) {
        this.io.unobserve(this.section);
        this.io.disconnect();
      }
      delete this.section.dataset.parallaxBound;
    }
  }

  function initSection(section) {
    if (!section || section.dataset.parallaxBound === 'true') return;
    const instance = new HeroParallax(section);
    INSTANCES.set(section, instance);
  }

  function destroySection(section) {
    const instance = INSTANCES.get(section);
    if (instance) {
      instance.destroy();
      INSTANCES.delete(section);
    }
  }

  function initAll() {
    // Inicializar ambas variantes
    document
      .querySelectorAll('[data-hero-parallax], [data-hero-parallax-v2]')
      .forEach(initSection);
  }

  // Init al cargar (script va con defer, pero por si acaso)
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  
  document.addEventListener('shopify:section:load', (e) => {
    e.target
      .querySelectorAll?.('[data-hero-parallax], [data-hero-parallax-v2]')
      .forEach(initSection);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    e.target
      .querySelectorAll?.('[data-hero-parallax], [data-hero-parallax-v2]')
      .forEach(destroySection);
  });

  document.addEventListener('shopify:section:select', (e) => {
    e.target
      .querySelectorAll?.('[data-hero-parallax], [data-hero-parallax-v2]')
      .forEach((el) => {
        const inst = INSTANCES.get(el);
        inst?.start();
      });
  });

  document.addEventListener('shopify:section:deselect', (e) => {
    e.target
      .querySelectorAll?.('[data-hero-parallax], [data-hero-parallax-v2]')
      .forEach((el) => {
        const inst = INSTANCES.get(el);
        inst?.stop();
      });
  });
})();
